import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { getAdminAccessToken } from '../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ReleaseNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const token = getAdminAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/release-notes?page=${currentPage}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(response.data.data?.releaseNotes || []);
      setTotalPages(response.data.data?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching release notes:', error);
      toast.error('Failed to fetch release notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleDelete = async (id) => {
    try {
      const token = getAdminAccessToken();
      await axios.delete(
        `${API_BASE_URL}/api/admin/release-notes/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Release note deleted');
      setDeleteConfirm(null);
      fetchNotes();
    } catch (error) {
      console.error('Error deleting release note:', error);
      toast.error('Failed to delete release note');
    }
  };

  const handleSave = async (formData) => {
    try {
      const token = getAdminAccessToken();
      const headers = { Authorization: `Bearer ${token}` };

      if (editingNote) {
        await axios.put(
          `${API_BASE_URL}/api/admin/release-notes/${editingNote.id}`,
          formData,
          { headers }
        );
        toast.success('Release note updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/admin/release-notes`,
          formData,
          { headers }
        );
        toast.success('Release note created');
      }

      setShowEditor(false);
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      console.error('Error saving release note:', error);
      toast.error('Failed to save release note');
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.title}</div>
          {row.version && <div className="text-xs text-gray-500 font-mono">v{row.version}</div>}
        </div>
      ),
    },
    {
      key: 'is_published',
      label: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          row.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {row.is_published ? 'Published' : 'Draft'}
        </span>
      ),
    },
    {
      key: 'features',
      label: 'Features',
      render: (row) => {
        const count = Array.isArray(row.features) ? row.features.length : 0;
        return <span className="text-sm text-gray-600">{count} feature{count !== 1 ? 's' : ''}</span>;
      },
    },
    {
      key: 'published_at',
      label: 'Published At',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.published_at ? new Date(row.published_at).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setEditingNote(row); setShowEditor(true); }}
            className="p-1 text-gray-400 hover:text-primary-600 rounded"
            title="Edit"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDeleteConfirm(row.id)}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Delete"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Release Notes</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage product release notes and feature announcements
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchNotes}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => { setEditingNote(null); setShowEditor(true); }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              New Release Note
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={notes}
            loading={loading}
            emptyMessage="No release notes yet. Create your first one!"
          />
        </div>

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Release Note?</h3>
              <p className="text-gray-600 mb-6">This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor Modal */}
        {showEditor && (
          <ReleaseNoteEditor
            note={editingNote}
            onSave={handleSave}
            onClose={() => { setShowEditor(false); setEditingNote(null); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

function ReleaseNoteEditor({ note, onSave, onClose }) {
  const [title, setTitle] = useState(note?.title || '');
  const [version, setVersion] = useState(note?.version || '');
  const [description, setDescription] = useState(note?.description || '');
  const [isPublished, setIsPublished] = useState(note?.is_published || false);
  const [publishedAt, setPublishedAt] = useState(
    note?.published_at ? new Date(note.published_at).toISOString().slice(0, 16) : ''
  );
  const [features, setFeatures] = useState(
    Array.isArray(note?.features) && note.features.length > 0
      ? note.features
      : [{ title: '', type: 'feature' }]
  );
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiPrompt, setShowAiPrompt] = useState(false);

  const hasContent = title.trim() || description.trim() || features.some(f => f.title?.trim());

  const handleAiGenerate = async (mode) => {
    setAiLoading(true);
    try {
      const token = getAdminAccessToken();
      const body = {
        mode,
        prompt: aiPrompt.trim() || undefined,
      };
      if (mode === 'improve') {
        body.title = title;
        body.version = version;
        body.description = description;
        body.features = features.filter(f => f.title?.trim());
      }
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/release-notes/ai/generate`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data.data;
      if (data.title) setTitle(data.title);
      if (data.version) setVersion(data.version);
      if (data.description) setDescription(data.description);
      if (Array.isArray(data.features) && data.features.length > 0) {
        setFeatures(data.features);
      }
      setShowAiPrompt(false);
      setAiPrompt('');
      toast.success(mode === 'generate' ? 'Release note generated!' : 'Release note improved!');
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error('AI generation failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        version: version.trim() || null,
        description: description.trim() || null,
        is_published: isPublished,
        published_at: publishedAt ? new Date(publishedAt).toISOString() : (isPublished ? new Date().toISOString() : null),
        features: features.filter((f) => f.title?.trim()),
      });
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFeatures([...features, { title: '', type: 'feature' }]);
  };

  const updateFeature = (idx, field, value) => {
    const updated = [...features];
    updated[idx] = { ...updated[idx], [field]: value };
    setFeatures(updated);
  };

  const removeFeature = (idx) => {
    setFeatures(features.filter((_, i) => i !== idx));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {note ? 'Edit Release Note' : 'New Release Note'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAiPrompt(!showAiPrompt)}
              disabled={aiLoading}
              className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              <SparklesIcon className={`w-4 h-4 ${aiLoading ? 'animate-spin' : ''}`} />
              AI
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {showAiPrompt && (
          <div className="mx-6 mt-4 p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800">
              <SparklesIcon className="w-4 h-4" />
              AI Assistant
            </div>
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Optional: describe what this release includes..."
              className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              disabled={aiLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAiGenerate(hasContent ? 'improve' : 'generate');
                }
              }}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleAiGenerate('generate')}
                disabled={aiLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              >
                {aiLoading ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate New
                  </>
                )}
              </button>
              {hasContent && (
                <button
                  type="button"
                  onClick={() => handleAiGenerate('improve')}
                  disabled={aiLoading}
                  className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-white border border-purple-300 hover:bg-purple-50 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  {aiLoading ? 'Working...' : 'Improve Current'}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setShowAiPrompt(false); setAiPrompt(''); }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. April 2026 Update"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g. 2.5.0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Brief summary of this release..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Features</label>
              <button
                type="button"
                onClick={addFeature}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <PlusIcon className="w-4 h-4" />
                Add feature
              </button>
            </div>
            <div className="space-y-2">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={feature.type}
                    onChange={(e) => updateFeature(idx, 'type', e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="feature">New</option>
                    <option value="improvement">Improved</option>
                    <option value="fix">Fixed</option>
                  </select>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => updateFeature(idx, 'title', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Feature description..."
                  />
                  {features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(idx)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
              <input
                type="datetime-local"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  Published
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? 'Saving...' : (note ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReleaseNotes;
